import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Chip,
  Button, Alert, TextField, Grid, Divider, MenuItem, Stack, Tooltip,
} from '@mui/material';
import {
  CheckCircle, Flag, Payments, RateReview, VerifiedUser, WarningAmber, Edit,
} from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';
import { isRealDate, diffDays } from '../utils/dateValidation';
import { fmtMoney } from '../utils/currency';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const currentUserName = () => {
  const u = JSON.parse(localStorage.getItem('user') || '{}');
  return u.fullName || u.username || u.email || '';
};
const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—';
const money = (n) => fmtMoney(n);
const TYPES = ['Resignation', 'Dismissal', 'Retirement', 'End of Contract', 'Death'];

const STATUS_COLOR = { Initiated: 'default', ClearanceInProgress: 'info', ClearanceComplete: 'primary', DuesProcessed: 'secondary', Released: 'success', Cancelled: 'error' };
const STATUS_LABEL = { Initiated: 'Initiated', ClearanceInProgress: 'Clearance In Progress', ClearanceComplete: 'Clearance Complete', DuesProcessed: 'Dues Processed', Released: 'Released', Cancelled: 'Cancelled' };

const computeNet = (d) => (Number(d.leavePayout) || 0) + (Number(d.pension) || 0) + (Number(d.gratuity) || 0) + (Number(d.outstandingToEmployee) || 0) - (Number(d.outstandingFromEmployee) || 0) - (Number(d.otherDeductions) || 0);

const ExitCard = ({ sepId, onClose, onChanged }) => {
  const [s, setS]       = useState(null);
  const [error, setError] = useState('');
  const [clearDlg, setClearDlg] = useState(null);  // { stage }
  const [editDlg, setEditDlg] = useState(null);    // edit-details form
  const { attemptClose, discardDialog } = useUnsavedGuard({ open: Boolean(editDlg), value: editDlg, onClose: () => setEditDlg(null) });
  const [dues, setDues] = useState({});
  const [interview, setInterview] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/exit/separations/${sepId}`, { headers: hdrs() });
      setS(r.data);
      setDues(r.data.terminalDues || {});
      setInterview(r.data.exitInterview || {});
    } catch (e) { setError(e.response?.data?.error || 'Failed to load'); }
  }, [sepId]);
  useEffect(() => { load(); }, [load]);
  const refresh = () => { load(); onChanged && onChanged(); };

  const post = async (path, body, okMsg) => {
    setBusy(true); setError('');
    try { await axios.post(`${API}/api/exit/separations/${s._id}/${path}`, body, { headers: hdrs() }); refresh(); return true; }
    catch (e) { setError(e.response?.data?.error || 'Action failed'); return false; }
    finally { setBusy(false); }
  };

  const openEdit = () => setEditDlg({
    type: s.type, nature: s.nature,
    noticeDate: s.noticeDate?.slice(0, 10) || '', lastWorkingDay: s.lastWorkingDay?.slice(0, 10) || '',
    noticePeriodDays: s.noticePeriodDays ?? '', reason: s.reason || '',
    description: s.description || '', disciplinaryCaseRef: s.disciplinaryCaseRef || '',
  });
  const setEditDate = (field, value) => {
    const nd = { ...editDlg, [field]: value };
    const np = diffDays(field === 'noticeDate' ? value : nd.noticeDate, field === 'lastWorkingDay' ? value : nd.lastWorkingDay);
    if (np !== '') nd.noticePeriodDays = np;
    setEditDlg(nd);
  };
  const saveEdit = async () => {
    setBusy(true); setError('');
    try { await axios.put(`${API}/api/exit/separations/${s._id}`, editDlg, { headers: hdrs() }); setEditDlg(null); refresh(); }
    catch (e) { setError(e.response?.data?.error || 'Failed to save details'); }
    finally { setBusy(false); }
  };

  if (!s) return <Dialog open fullWidth maxWidth="md"><DialogContent>{error ? <Alert severity="error">{error}</Alert> : 'Loading…'}</DialogContent></Dialog>;

  const firstPending = s.clearance.find(c => c.status !== 'Cleared');
  const allCleared = s.clearance.every(c => c.status === 'Cleared');
  const isDismissal = s.type === 'Dismissal';
  const locked = s.status === 'Released';
  const netDue = computeNet(dues);
  // Details can be edited only at the initial stage — before any department has acted
  const canEditDetails = !locked && s.clearance.every(c => c.status === 'Pending');

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle component="div">
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Box>
            <Typography variant="h6" component="div" fontWeight={700}>{s.employeeName} <Typography component="span" variant="body2" color="text.secondary">({s.employeeNo})</Typography></Typography>
            <Typography variant="caption" component="div" color="text.secondary">{s.sepNo} · {s.department || '—'} · Last day: {fmtDate(s.lastWorkingDay)}</Typography>
          </Box>
          <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
            <Chip label={s.type} size="small" color={isDismissal ? 'error' : 'default'} />
            <Chip label={s.nature} size="small" variant="outlined" />
            <Chip label={STATUS_LABEL[s.status]} size="small" color={STATUS_COLOR[s.status] || 'default'} />
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {s.certificateIssued && <Alert severity="success" icon={<VerifiedUser />} sx={{ mb: 2 }}>Released {fmtDate(s.releasedDate)} — Certificate of Service issued.</Alert>}

        {/* Details */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
          <Typography variant="subtitle2" fontWeight={700} color="text.secondary">Separation Details</Typography>
          {canEditDetails && <Button size="small" startIcon={<Edit />} onClick={openEdit}>Edit Details</Button>}
        </Box>
        <Grid container spacing={1.5} mb={2}>
          <Grid item xs={6}><Typography variant="caption" color="text.secondary">Notice Date</Typography><Typography variant="body2">{fmtDate(s.noticeDate)}</Typography></Grid>
          <Grid item xs={6}><Typography variant="caption" color="text.secondary">Notice Period</Typography><Typography variant="body2">{s.noticePeriodDays ? `${s.noticePeriodDays} days` : '—'}</Typography></Grid>
          <Grid item xs={12}><Typography variant="caption" color="text.secondary">Reason</Typography><Typography variant="body2">{s.reason || '—'}</Typography></Grid>
          {s.description && <Grid item xs={12}><Typography variant="caption" color="text.secondary">Description</Typography><Typography variant="body2">{s.description}</Typography></Grid>}
          {s.disciplinaryCaseRef && <Grid item xs={12}><Typography variant="caption" color="text.secondary">Disciplinary Case</Typography><Typography variant="body2">{s.disciplinaryCaseRef}</Typography></Grid>}
        </Grid>

        {/* Clearance (ordered) */}
        <Divider sx={{ mb: 1.5 }} />
        <Typography variant="subtitle1" fontWeight={700} mb={1}>Clearance — ordered</Typography>
        <Stack spacing={1} mb={2}>
          {s.clearance.map((c) => (
            <Box key={c.stage} display="flex" alignItems="center" justifyContent="space-between" sx={{ border: '1px solid #e8eaf0', borderRadius: 2, p: 1.2 }}>
              <Box display="flex" alignItems="center" gap={1.5}>
                <Chip label={c.order} size="small" sx={{ bgcolor: '#eceff1' }} />
                <Box>
                  <Typography variant="body2" fontWeight={600}>{c.stage}</Typography>
                  {c.status === 'Cleared' && <Typography variant="caption" color="text.secondary">Cleared by {c.clearedBy} · {fmtDate(c.clearedDate)}{c.remarks ? ` · ${c.remarks}` : ''}</Typography>}
                </Box>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Chip label={c.status} size="small" color={c.status === 'Cleared' ? 'success' : c.status === 'Flagged' ? 'error' : 'default'} variant={c.status === 'Pending' ? 'outlined' : 'filled'} />
                {!locked && c.status !== 'Cleared' && (
                  <Tooltip title={firstPending && firstPending.stage === c.stage ? 'Clear this stage' : 'Clear earlier stages first'}>
                    <span><Button size="small" variant="contained" disabled={!firstPending || firstPending.stage !== c.stage} onClick={() => setClearDlg({ stage: c.stage, remarks: '', clearedBy: currentUserName() })}>Clear</Button></span>
                  </Tooltip>
                )}
              </Box>
            </Box>
          ))}
        </Stack>

        {/* Terminal dues */}
        <Divider sx={{ mb: 1.5 }} />
        <Box display="flex" alignItems="center" gap={1} mb={1}><Payments color="primary" fontSize="small" /><Typography variant="subtitle1" fontWeight={700}>Terminal Dues</Typography>{s.terminalDues?.approved && <Chip label="Approved" size="small" color="success" />}</Box>
        <Grid container spacing={1.5} mb={1}>
          {[['leaveBalanceDays', 'Leave Balance (days)'], ['leavePayout', 'Leave Payout'], ['pension', 'Pension'], ['gratuity', 'Gratuity'], ['outstandingToEmployee', 'Dues to Employee'], ['outstandingFromEmployee', 'Dues from Employee'], ['otherDeductions', 'Other Deductions']].map(([k, label]) => (
            <Grid item xs={6} sm={4} key={k}>
              <TextField type="number" label={label} size="small" fullWidth value={dues[k] ?? 0} disabled={locked || s.terminalDues?.approved}
                onChange={e => setDues({ ...dues, [k]: e.target.value })} />
            </Grid>
          ))}
          <Grid item xs={6} sm={4}>
            <Box sx={{ bgcolor: '#e8f5e9', borderRadius: 1, p: 1, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">Net Payable</Typography>
              <Typography variant="subtitle1" fontWeight={800} color="#2e7d32">{money(netDue)}</Typography>
            </Box>
          </Grid>
        </Grid>
        {!locked && !s.terminalDues?.approved && (
          <Stack direction="row" spacing={1} mb={2}>
            <Button size="small" variant="outlined" disabled={busy} onClick={() => post('terminal-dues', dues)}>Save Dues</Button>
            <Button size="small" variant="contained" color="success" disabled={busy} onClick={() => post('terminal-dues', { ...dues, approved: true })}>Approve & Process</Button>
          </Stack>
        )}

        {/* Exit interview */}
        <Divider sx={{ mb: 1.5 }} />
        <Box display="flex" alignItems="center" gap={1} mb={1}><RateReview color="primary" fontSize="small" /><Typography variant="subtitle1" fontWeight={700}>Exit Interview</Typography></Box>
        {isDismissal ? (
          <Alert severity="info" icon={<WarningAmber />}>Exit interview is not required for a dismissal.</Alert>
        ) : s.exitInterview?.submittedDate ? (
          <Box sx={{ bgcolor: '#fafafa', borderRadius: 2, p: 1.5 }}>
            <Typography variant="caption" color="text.secondary">Submitted {fmtDate(s.exitInterview.submittedDate)}</Typography>
            <Typography variant="body2"><strong>Reason for leaving:</strong> {s.exitInterview.reasonForLeaving || '—'}</Typography>
            <Typography variant="body2"><strong>Would recommend:</strong> {s.exitInterview.wouldRecommend || '—'}</Typography>
            {s.exitInterview.bestAspects && <Typography variant="body2"><strong>Best aspects:</strong> {s.exitInterview.bestAspects}</Typography>}
            {s.exitInterview.improvementAreas && <Typography variant="body2"><strong>Improvements:</strong> {s.exitInterview.improvementAreas}</Typography>}
            {s.exitInterview.additionalComments && <Typography variant="body2"><strong>Comments:</strong> {s.exitInterview.additionalComments}</Typography>}
          </Box>
        ) : (
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={8}><TextField label="Reason for leaving" size="small" fullWidth value={interview.reasonForLeaving || ''} onChange={e => setInterview({ ...interview, reasonForLeaving: e.target.value })} /></Grid>
            <Grid item xs={12} sm={4}><TextField select label="Would recommend?" size="small" fullWidth value={interview.wouldRecommend || ''} onChange={e => setInterview({ ...interview, wouldRecommend: e.target.value })}>
              {['Yes', 'No', 'Maybe'].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
            </TextField></Grid>
            <Grid item xs={12} sm={6}><TextField label="Best aspects" size="small" fullWidth value={interview.bestAspects || ''} onChange={e => setInterview({ ...interview, bestAspects: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField label="Areas for improvement" size="small" fullWidth value={interview.improvementAreas || ''} onChange={e => setInterview({ ...interview, improvementAreas: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField label="Additional comments" size="small" fullWidth multiline rows={2} value={interview.additionalComments || ''} onChange={e => setInterview({ ...interview, additionalComments: e.target.value })} /></Grid>
            <Grid item xs={12}><Button size="small" variant="outlined" disabled={busy} onClick={() => post('exit-interview', interview)}>Submit Exit Interview</Button></Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ flexWrap: 'wrap', gap: 0.5, px: 2 }}>
        {!locked && (
          <Tooltip title={allCleared ? 'Release & issue Certificate of Service' : 'Complete all clearance stages first'}>
            <span><Button variant="contained" color="success" startIcon={<VerifiedUser />} disabled={busy || !allCleared} onClick={() => post('release')}>Release &amp; Issue Certificate</Button></span>
          </Tooltip>
        )}
        {locked && <Button variant="outlined" startIcon={<VerifiedUser />} onClick={() => window.print()}>Print Certificate</Button>}
        <Box flex={1} />
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* Clear stage dialog */}
      <Dialog open={Boolean(clearDlg)} onClose={() => setClearDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Clear: {clearDlg?.stage}</DialogTitle>
        <DialogContent dividers>
          {clearDlg && (
            <Stack spacing={2} pt={1}>
              <TextField label="Cleared by" size="small" value={clearDlg.clearedBy} onChange={e => setClearDlg({ ...clearDlg, clearedBy: e.target.value })} placeholder="Officer name (optional)" />
              <TextField label="Remarks" size="small" multiline rows={2} value={clearDlg.remarks} onChange={e => setClearDlg({ ...clearDlg, remarks: e.target.value })} />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="error" startIcon={<Flag />} onClick={async () => { if (await post('clearance', { stage: clearDlg.stage, status: 'Flagged', remarks: clearDlg.remarks, clearedBy: clearDlg.clearedBy })) setClearDlg(null); }}>Flag</Button>
          <Box flex={1} />
          <Button onClick={() => setClearDlg(null)}>Cancel</Button>
          <Button variant="contained" startIcon={<CheckCircle />} onClick={async () => { if (await post('clearance', { stage: clearDlg.stage, status: 'Cleared', remarks: clearDlg.remarks, clearedBy: clearDlg.clearedBy })) setClearDlg(null); }}>Clear</Button>
        </DialogActions>
      </Dialog>

      {/* Edit details dialog (initial stage only) */}
      {discardDialog}
      <Dialog open={Boolean(editDlg)} onClose={attemptClose} maxWidth="md" fullWidth>
        <DialogTitle>Edit Separation Details</DialogTitle>
        <DialogContent dividers>
          {editDlg && (() => {
            const bothDates = !!(editDlg.noticeDate && editDlg.lastWorkingDay);
            const noticeInvalid = !!editDlg.noticeDate && !isRealDate(editDlg.noticeDate);
            const lastInvalid = !!editDlg.lastWorkingDay && !isRealDate(editDlg.lastWorkingDay);
            const orderErr = bothDates && !noticeInvalid && !lastInvalid && new Date(editDlg.lastWorkingDay) < new Date(editDlg.noticeDate);
            const dErr = lastInvalid || orderErr;
            return (
              <Grid container spacing={2.5} sx={{ pt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <TextField select label="Separation Type" value={editDlg.type} fullWidth
                    onChange={e => setEditDlg({ ...editDlg, type: e.target.value, nature: e.target.value === 'Dismissal' ? 'Involuntary' : editDlg.nature })}>
                    {TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField select label="Nature" value={editDlg.nature} fullWidth onChange={e => setEditDlg({ ...editDlg, nature: e.target.value })}>
                    {['Voluntary', 'Involuntary'].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}><TextField type="date" label="Notice Date" InputLabelProps={{ shrink: true }} value={editDlg.noticeDate} onChange={e => setEditDate('noticeDate', e.target.value)} fullWidth size="small"
                  error={noticeInvalid} helperText={noticeInvalid ? 'Not a valid date' : ' '} /></Grid>
                <Grid item xs={12} sm={4}>
                  <TextField type="date" label="Last Working Day" InputLabelProps={{ shrink: true }} value={editDlg.lastWorkingDay} onChange={e => setEditDate('lastWorkingDay', e.target.value)} fullWidth size="small"
                    inputProps={{ min: editDlg.noticeDate || undefined }} error={dErr} helperText={lastInvalid ? 'Not a valid date' : orderErr ? 'Cannot be before the notice date' : ' '} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField type="number" label="Notice Period (days)" value={editDlg.noticePeriodDays} onChange={e => setEditDlg({ ...editDlg, noticePeriodDays: e.target.value })} fullWidth size="small"
                    InputProps={{ readOnly: bothDates }} helperText={bothDates ? 'Auto-calculated from dates' : ''} />
                </Grid>
                <Grid item xs={12}><TextField label="Reason" value={editDlg.reason} onChange={e => setEditDlg({ ...editDlg, reason: e.target.value })} fullWidth size="small" /></Grid>
                <Grid item xs={12}><TextField label="Description" value={editDlg.description} onChange={e => setEditDlg({ ...editDlg, description: e.target.value })} fullWidth size="small" multiline rows={2} /></Grid>
                {editDlg.type === 'Dismissal' && <Grid item xs={12}><TextField label="Disciplinary Case Ref" value={editDlg.disciplinaryCaseRef} onChange={e => setEditDlg({ ...editDlg, disciplinaryCaseRef: e.target.value })} fullWidth size="small" placeholder="e.g. DISC-00001" /></Grid>}
              </Grid>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={saveEdit} disabled={busy || !!(editDlg && (
            (editDlg.noticeDate && !isRealDate(editDlg.noticeDate)) ||
            (editDlg.lastWorkingDay && !isRealDate(editDlg.lastWorkingDay)) ||
            (editDlg.noticeDate && editDlg.lastWorkingDay && new Date(editDlg.lastWorkingDay) < new Date(editDlg.noticeDate))
          ))}>Save</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default ExitCard;
